// Phone cameras produce 3-15 MB JPEGs. Uploading them as-is over cellular
// takes 30-90s per photo, which is a setup for client disconnects. Resize
// before upload to keep things in the 200-500 KB range.

export interface Dimensions {
  width: number;
  height: number;
}

export function resizeDimensions(
  source: Dimensions,
  maxDim: number,
): Dimensions {
  const scale = Math.min(1, maxDim / Math.max(source.width, source.height));
  return {
    width: Math.max(1, Math.round(source.width * scale)),
    height: Math.max(1, Math.round(source.height * scale)),
  };
}

export async function resizeImage(
  file: File,
  maxDim = 2400,
  quality = 0.92,
): Promise<Blob> {
  const bitmap = await createImageBitmap(file, {
    imageOrientation: "from-image",
  });

  try {
    const target = resizeDimensions(
      { width: bitmap.width, height: bitmap.height },
      maxDim,
    );

    const canvas = document.createElement("canvas");
    canvas.width = target.width;
    canvas.height = target.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas 2D context not available");
    }
    ctx.drawImage(bitmap, 0, 0, target.width, target.height);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob
            ? resolve(blob)
            : reject(new Error("Canvas toBlob returned null")),
        "image/jpeg",
        quality,
      );
    });
  } finally {
    bitmap.close();
  }
}
